// Agent run control (ADR-0021 §4, UI_DATA §8.4): Pause · Resume · Steer · Take over · Stop, each one catalogue command through
// the inbox (run.pause, run.resume, run.message, run.takeover, run.end --outcome abandoned). Whether each act is open now, and
// why not, is the engine's (`agent_runs.data.controls.acts`); the browser only shows it. The run's control history (pauses,
// messages with when the agent read them, a take-over) is shown in the run's timeline. Only people control runs, never agents.
import type { TimelineEvent } from "../components";
import { StatusPill } from "../components";
import type { AgentRun, RunAct } from "../data/types";
import { fmtStamp, plural } from "../lib/format";
import { Act, type Open } from "./engagement/shared";

const ACTS: Array<{ act: RunAct; command: string; label: string; title: (id: string) => string; fixed?: Record<string, unknown>; kind?: "danger" }> = [
  { act: "pause", command: "run.pause", label: "Pause…", title: (id) => `Pause run ${id} — say why` },
  { act: "resume", command: "run.resume", label: "Resume…", title: (id) => `Resume run ${id}` },
  { act: "steer", command: "run.message", label: "Steer…", title: (id) => `Steer run ${id} — a message the agent must read first` },
  { act: "take_over", command: "run.takeover", label: "Take over…", title: (id) => `Take over run ${id} — say why` },
  { act: "stop", command: "run.end", label: "Stop…", title: (id) => `Stop run ${id} — say why in your own words`, fixed: { outcome: "abandoned" }, kind: "danger" },
];
const ACT_WORD: Record<RunAct, string> = { pause: "Pause", resume: "Resume", steer: "Steer", take_over: "Take over", stop: "Stop" };

/** Why an act is not open now: the engine's reason; without published controls, nothing is offered (never guessed). */
export function actBlocked(r: AgentRun, act: RunAct): string | null {
  if (!r.controls) return "The engine has not published controls for this run (run.control).";
  const c = r.controls.acts[act];
  if (!c) return `The engine does not offer ${ACT_WORD[act].toLowerCase()} for this run.`;
  return c.allowed ? null : c.why ?? "Not open now.";
}

export function RunControlButtons({ r, open }: { r: AgentRun; open: Open }) {
  const shown = ACTS.map((a) => [a, actBlocked(r, a.act)] as const);
  return (
    <div className="space-y-1">
      <div role="group" aria-label={`Controls for run ${r.id}`} className="flex flex-wrap items-center gap-1.5">
        {shown.map(([a, why]) => (
          <Act key={a.act} command={a.command} title={a.title(r.id)} open={open} fixed={{ run: Number(r.id), ...a.fixed }} label={a.label} kind={a.kind} reason={why} />
        ))}
      </div>
      {r.controls && shown.some(([, why]) => why) && (
        <p className="text-xs text-muted">{shown.filter(([, why]) => why).map(([a, why]) => `${ACT_WORD[a.act]}: ${why}`).join(" · ")}</p>
      )}
      {r.controls?.who && <p className="text-[11px] text-muted">Who may control it: {r.controls.who}</p>}
    </div>
  );
}

/** The run's control state as published: paused (by, when, why), messages the agent has not read yet, taken over. */
export function RunControlState({ r }: { r: AgentRun }) {
  const c = r.controls;
  if (!c) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {c.paused && <StatusPill state="paused" label={`Paused by ${c.paused.by}`} title={c.paused.reason ?? undefined} />}
      {c.paused?.reason && <span className="text-muted">“{c.paused.reason}” · {fmtStamp(c.paused.at)}</span>}
      {c.unread > 0 && <StatusPill state="waiting" label={`${plural(c.unread, "message")} not yet read by the agent`} />}
      {c.takenOver && <StatusPill state="taken_over" label={`Taken over by ${c.takenOver.by} · ${fmtStamp(c.takenOver.at)}`} />}
    </div>
  );
}

const KIND: Record<string, string> = { message: "Steered", pause: "Paused", resume: "Resumed", takeover: "Taken over", take_over: "Taken over", stop: "Stopped" };
/** Control history as timeline events (a person's acts; a message shows whether the agent has read it). */
export function controlEvents(r: AgentRun, prefix = ""): TimelineEvent[] {
  return (r.controls?.history ?? []).map((h) => ({
    id: `${prefix}${r.id}h${h.id}`, at: h.at, tone: "person" as const,
    title: <span>{prefix ? `Run ${r.id}: ` : ""}{KIND[h.kind] ?? h.kind} by {h.by}</span>,
    detail: <span>{h.text && <>“{h.text}”</>}{h.kind === "message" && <span className="ml-1">{h.readAt ? `· read by the agent ${fmtStamp(h.readAt)}` : "· not read by the agent yet"}</span>}</span>,
  }));
}
